/**
 * Lista de templates Meta WhatsApp (Brief 4 · Tela 3 · PR1 leitura).
 *
 * Tabela com status semáforo PT + badge D-6 + contagem de variáveis + data de
 * submissão. Banner "Tech Provider Meta pendente" é honest-defer (sem fonte de
 * estado de onboarding ainda — nota distinta de "em breve", não dado fabricado).
 * "+ Novo template" e "Editar" só aparecem com `canManage` (gate
 * `message-template-manage`); a ação real chega no PR2.
 */

import { AlertTriangle } from 'lucide-react';

import { categoryLabel, categorySemaforo, statusMeta } from './labels';
import { countVariables, flattenComponents, formatShortDate } from './template-helpers';

import type { TemplateDto } from './types';

interface TemplateListViewProps {
  templates: TemplateDto[];
  canManage: boolean;
  onOpen: (id: string) => void;
  onCreate: () => void;
}

export function TemplateListView({
  templates,
  canManage,
  onOpen,
  onCreate,
}: TemplateListViewProps): JSX.Element {
  return (
    <div className="message-templates-screen">
      <div className="mt-pagehead">
        <div className="mt-pagehead__heading">
          <div className="mt-pagehead__crumbs">Produto</div>
          <h1 className="mt-pagehead__title">Templates Meta WhatsApp</h1>
          <p className="mt-pagehead__sub">
            Mensagens HSM aprovadas pela Meta para envio fora da janela de 24h.
          </p>
        </div>
        <div className="mt-pagehead__spacer" />
        {canManage && (
          <button type="button" className="mt-btn mt-btn--primary" onClick={onCreate}>
            + Novo template
          </button>
        )}
      </div>

      <div className="mt-page">
        {/* honest-defer: estado de onboarding Meta (Tech Provider) não tem fonte ainda. */}
        <div className="mt-banner mt-banner--warning" role="note">
          <AlertTriangle size={14} aria-hidden />
          <span>
            <strong>Cadastro Tech Provider Meta</strong> — o status de onboarding ainda não é
            exposto por aqui. Templates podem ser criados e editados, mas a submissão à Meta requer
            o cadastro ativo (configuração de ambiente).
          </span>
        </div>

        {templates.length === 0 ? (
          <div className="mt-empty" role="status">
            Nenhum template configurado ainda.
            {canManage && ' Crie o primeiro com "+ Novo template".'}
          </div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Categoria</th>
                <th>Idioma</th>
                <th>Status Meta</th>
                <th className="mt-table__num">Variáveis</th>
                <th>Submetido</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl) => {
                const meta = statusMeta(tpl.status);
                const flat = flattenComponents(tpl.components);
                const vars = countVariables(flat.body);
                return (
                  <tr key={tpl.id} className="mt-table__row">
                    <td>
                      <span className="mt-table__name">{tpl.name}</span>
                      {tpl.isD6 && (
                        <span
                          className="mt-d6-badge"
                          title="Aviso D-6 — enviado ao cliente antes do fechamento automático"
                        >
                          D-6
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`mt-pill mt-pill--${categorySemaforo(tpl.category)}`}>
                        {categoryLabel(tpl.category)}
                      </span>
                    </td>
                    <td className="mt-mono mt-fg3">{tpl.language}</td>
                    <td>
                      <span className={`mt-pill mt-pill--${meta.semaforo}`}>
                        {meta.glyph} {meta.label}
                      </span>
                    </td>
                    <td className="mt-table__num mt-mono">{vars}</td>
                    <td className="mt-mono mt-fg3">{formatShortDate(tpl.submittedAt)}</td>
                    <td className="mt-table__actions">
                      <button
                        type="button"
                        className="mt-btn mt-btn--sm"
                        onClick={() => onOpen(tpl.id)}
                      >
                        {canManage ? 'Editar' : 'Ver'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
